"use client";

import { AnimatePresence, useInView } from "framer-motion";
import { Mail, MessageSquare, Split, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { BRAND_LOGO } from "../../constants";
import type { SceneProps } from "../../types";
import { useSceneFinish } from "../../use-scene-finish";
import { CAPTURE_LEADS, type Lead, LeadCard } from "./lead-inbox";
import {
  BranchBadge,
  Connector,
  FirstNamePill,
  ForkBranch,
  NodeHead,
  type NodeState,
  NodeShell,
} from "./workflow";

// The trace advances one phase at a time; each phase animates a single segment
// (a node perimeter or a connector) so the line reads as one continuous draw.
// [prepend, trigger, link, condition, branch, action, done/hold]
const PHASE_MS = [900, 1050, 600, 1050, 1150, 1050, 1600];
const FINAL_PHASE = 6;

// The action card for one branch (the SMS draft or the email draft).
function BranchCard({
  tone,
  state,
  lead,
  resolved,
  reduce,
}: {
  tone: "yes" | "no";
  state: NodeState;
  lead: Lead;
  resolved: boolean;
  reduce: boolean;
}) {
  return (
    <NodeShell state={state} reduce={reduce} className="w-full max-w-[320px]">
      {tone === "yes" ? (
        <>
          <NodeHead
            icon={MessageSquare}
            kind="Action"
            title="Send text message"
            iconClass="text-[hsl(var(--outgoing-bubble))]"
            state={state}
            doneLabel="Sent"
            reduce={reduce}
          />
          <div className="border-t border-border px-3.5 pb-3.5 pt-2.5">
            <p className="mb-1.5 text-[11px] font-medium text-muted-foreground">Message</p>
            <div className="flex justify-end">
              <div className="max-w-[250px] rounded-2xl rounded-br-md bg-outgoing-bubble px-3 py-2 text-[13px] leading-snug text-white">
                Hi <FirstNamePill resolved={resolved} name={lead.firstName} tone="light" />! Thanks
                for reaching out to Fitt Pilates. Want to claim your first class free? Reply YES and
                I&rsquo;ll get you booked.
              </div>
            </div>
          </div>
        </>
      ) : (
        <>
          <NodeHead
            icon={Mail}
            kind="Action"
            title="Send email"
            state={state}
            doneLabel="Sent"
            reduce={reduce}
          />
          <div className="border-t border-border px-3.5 pb-3.5 pt-2.5">
            <p className="mb-1 text-[11px] font-medium text-muted-foreground">Subject</p>
            <p className="text-[13px] font-semibold leading-snug text-foreground">
              <FirstNamePill resolved={resolved} name={lead.firstName} tone="violet" />, your free
              class at Fitt Pilates
            </p>
            <div className="mt-2.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
              <div className="flex items-center gap-1.5">
                <img
                  src={BRAND_LOGO}
                  alt="Fitt Pilates"
                  className="h-4 w-4 shrink-0 rounded-[4px] object-cover"
                />
                <span className="text-[10px] font-bold uppercase tracking-wide text-foreground">
                  Fitt Pilates
                </span>
              </div>
              <p className="mt-1.5 text-[13px] font-semibold text-foreground">
                Your first class is on us
              </p>
              <span className="mt-2 inline-block rounded-md bg-primary px-2.5 py-1 text-[11px] font-semibold text-primary-foreground">
                Claim free class
              </span>
            </div>
          </div>
        </>
      )}
    </NodeShell>
  );
}

// Desktop branch: badge stacked over its full action card (one per path).
function BranchColumn({
  tone,
  state,
  lead,
  resolved,
  reduce,
}: {
  tone: "yes" | "no";
  state: NodeState;
  lead: Lead;
  resolved: boolean;
  reduce: boolean;
}) {
  return (
    <div className="flex min-w-0 flex-1 flex-col items-center gap-2">
      <BranchBadge tone={tone} active={state !== "idle"} />
      <BranchCard tone={tone} state={state} lead={lead} resolved={resolved} reduce={reduce} />
    </div>
  );
}

export function CaptureLeads({ reduce, paused = false, onComplete }: SceneProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const inView = useInView(rootRef, { amount: 0.3 });
  const [leadIndex, setLeadIndex] = useState(0);
  const [phase, setPhase] = useState(0);

  const lastLead = CAPTURE_LEADS.length - 1;
  const complete = leadIndex >= lastLead && phase >= FINAL_PHASE;

  // Linger on the finished flow, then hand off to the next use case.
  useSceneFinish({
    active: complete && inView && !paused,
    delay: 1800,
    enabled: !reduce,
    onComplete,
  });

  // Each lead runs the flow once; then the next lead is prepended and re-runs it.
  // Plays while in view, pauses off-screen or when explicitly paused, and
  // freezes after the last lead.
  useEffect(() => {
    if (reduce || paused || !inView || complete) return;
    const id = setTimeout(() => {
      if (phase >= FINAL_PHASE) {
        setLeadIndex((i) => i + 1);
        setPhase(0);
      } else {
        setPhase((p) => p + 1);
      }
    }, PHASE_MS[phase]);
    return () => clearTimeout(id);
  }, [inView, phase, leadIndex, reduce, paused, complete]);

  const effLead = reduce ? lastLead : leadIndex;
  const effPhase = reduce ? FINAL_PHASE : phase;
  const started = reduce || inView || effPhase > 0 || effLead > 0;
  const resolved = effPhase >= 1;

  const activeLead = CAPTURE_LEADS[effLead];
  const chosen: "yes" | "no" = activeLead.optedIn ? "yes" : "no";

  // Newest processed lead on top; only leads handled so far are shown.
  const shown = started ? CAPTURE_LEADS.slice(0, effLead + 1).toReversed() : [];

  const triggerState: NodeState = effPhase === 1 ? "running" : effPhase >= 2 ? "done" : "idle";
  const condState: NodeState = effPhase === 3 ? "running" : effPhase >= 4 ? "done" : "idle";
  const actionState: NodeState = effPhase === 5 ? "running" : effPhase >= 6 ? "done" : "idle";
  const smsState: NodeState = chosen === "yes" ? actionState : "idle";
  const emailState: NodeState = chosen === "no" ? actionState : "idle";
  const link1Active = effPhase >= 2; // trigger → condition
  const link2Active = effPhase >= 4; // condition → branch

  return (
    <div
      ref={rootRef}
      className="grid items-stretch gap-6 p-4 sm:p-6 lg:grid-cols-[340px_1fr] lg:gap-10"
    >
      {/* Inbox: new leads — mirrors the in-app kanban column, sized to the flow */}
      <div className="flex flex-col rounded-xl bg-inset p-3">
        <div className="flex items-center justify-between gap-2 px-1 pb-3">
          <h3 className="flex items-center gap-2 text-base font-semibold text-foreground">
            New leads
            <Badge variant="outline" className="bg-background">
              {shown.length}
            </Badge>
          </h3>
          <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-500" /> Live
          </span>
        </div>
        {/* Desktop: full stack of processed leads, newest on top */}
        <div className="hidden flex-col gap-2 lg:flex">
          <AnimatePresence mode="popLayout" initial={false}>
            {shown.map((lead, i) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                active={lead.id === activeLead.id && started}
                position={i}
                reduce={reduce}
              />
            ))}
          </AnimatePresence>
        </div>
        {/* Mobile: only the active lead — the count badge signals there are more */}
        <div className="lg:hidden">
          <AnimatePresence mode="wait" initial={false}>
            {started ? (
              <LeadCard
                key={activeLead.id}
                lead={activeLead}
                active={started}
                position={0}
                reduce={reduce}
              />
            ) : null}
          </AnimatePresence>
        </div>
      </div>

      {/* Workflow */}
      <div className="mx-auto flex w-full min-w-0 max-w-[600px] flex-col">
        <div className="mx-auto w-full max-w-[340px]">
          <NodeShell state={triggerState} reduce={reduce}>
            <NodeHead
              icon={Zap}
              kind="Trigger"
              title="New lead created"
              state={triggerState}
              reduce={reduce}
            />
          </NodeShell>
        </div>

        <Connector active={link1Active} reduce={reduce} className="h-5" />

        <div className="mx-auto w-full max-w-[340px]">
          <NodeShell state={condState} reduce={reduce}>
            <NodeHead
              icon={Split}
              kind="Condition"
              title="Is opted into SMS?"
              state={condState}
              doneLabel={chosen === "yes" ? "Yes" : "No"}
              doneNegative={chosen === "no"}
              reduce={reduce}
            />
          </NodeShell>
        </div>

        <Connector active={link2Active} reduce={reduce} className="h-4" />
        {/* Branch fork — only the chosen side is traced */}
        <div className="relative h-3">
          <div className="absolute left-1/4 right-1/4 top-0 h-px rounded-full bg-border" />
          <ForkBranch side="left" active={chosen === "yes" && link2Active} reduce={reduce} />
          <ForkBranch side="right" active={chosen === "no" && link2Active} reduce={reduce} />
          <Connector
            active={chosen === "yes" && link2Active}
            reduce={reduce}
            delay={0.85}
            className="absolute left-1/4 top-0 h-3 -translate-x-1/2"
          />
          <Connector
            active={chosen === "no" && link2Active}
            reduce={reduce}
            delay={0.85}
            className="absolute right-1/4 top-0 h-3 translate-x-1/2"
          />
        </div>

        {/* Desktop: full tree — both paths with their action cards side-by-side */}
        <div className="mt-3 hidden lg:flex lg:flex-row lg:items-start lg:gap-6">
          <BranchColumn
            tone="yes"
            state={smsState}
            lead={activeLead}
            resolved={resolved}
            reduce={reduce}
          />
          <BranchColumn
            tone="no"
            state={emailState}
            lead={activeLead}
            resolved={resolved}
            reduce={reduce}
          />
        </div>

        {/* Mobile: YES/NO labels show the decision (Yes taken, No possible); only
            the chosen path's message card renders below to keep things compact. */}
        <div className="lg:hidden">
          <div className="mt-3 flex flex-row items-start gap-3">
            <div className="flex min-w-0 flex-1 justify-center">
              <BranchBadge tone="yes" active={chosen === "yes" && link2Active} />
            </div>
            <div className="flex min-w-0 flex-1 justify-center">
              <BranchBadge tone="no" active={chosen === "no" && link2Active} />
            </div>
          </div>
          <div className="mt-3 flex justify-center">
            <BranchCard
              tone={chosen}
              state={chosen === "yes" ? smsState : emailState}
              lead={activeLead}
              resolved={resolved}
              reduce={reduce}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
